/**
 * Un hilo de la ficha: lista, mensajes compactos y paso a paso (spec 024 · FR-004).
 * El hilo elegido se vuelve a pedir mientras se mira (033 · FR-006).
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { AvisoCitaAgenda, type CitaEnAgenda } from '../../components/AvisoCitaAgenda'
import { ControlDelHilo } from '../../components/ControlDelHilo'
import { MensajesConversacion } from '../../components/MensajesConversacion'
import { Badge } from '../../components/ui/badge'
import { api } from '../../lib/api'
import { emparejarPasosMensajes } from '../../lib/camino-mensaje'
import { cn } from '../../lib/cn'
import { CAJA_CONSEGUIDO, PUNTO_CONSEGUIDO, TEXTO_CONSEGUIDO, type Conseguido } from '../../lib/conseguido'
import { cuandoCita, fechaHora, hora } from '../../lib/tiempo'
import { useRefrescoHiloAbierto } from '../../lib/use-refresco-hilo'
import type { EventoAuditoriaVista, HandoverVista, SobreMensajes } from '../../lib/tipos'

export interface HiloLista {
    id: number
    estado: string | null
    ultima: string | null
}

interface PasoCamino {
    etiqueta: string
    at: string | null
    tono: Conseguido | null
    hecho?: string
}

interface GestionHilo {
    tipo: Conseguido
    fecha: string | null
    hora: string | null
    servicio: string | null
    profesional: string | null
}

interface DetalleHilo {
    messages: SobreMensajes
    camino: PasoCamino[]
    gestion: GestionHilo | null
    citaEnAgenda: CitaEnAgenda
    handovers: HandoverVista[]
    audit: EventoAuditoriaVista[]
    mando: 'agendify' | 'equipo' | 'cerrada' | null
}

const ETIQUETA_EVENTO: Record<string, string> = {
    APPOINTMENT_CREATED: 'Cita creada',
    APPOINTMENT_MODIFIED: 'Cita modificada',
    APPOINTMENT_CANCELLED: 'Cita cancelada',
    APPOINTMENT_REVERTED: 'Cita revertida',
    HOLD_ACQUIRED: 'Hueco reservado',
    HOLD_RELEASED: 'Hueco liberado',
    SLOT_COLLISION_CHECK: 'Comprobación de hueco',
    GDPR_CONSENT_GRANTED: 'Consentimiento RGPD',
    NEW_USER_REGISTERED: 'Alta de usuario',
}

export function ListaHilos({
    hilos,
    elegido,
    alElegir,
}: {
    hilos: HiloLista[]
    elegido: string | null
    alElegir: (id: number) => void
}) {
    if (hilos.length === 0) {
        return <p className="text-sm text-neutral-500">Aún no hay hilos en el canal.</p>
    }
    return (
        <ul className="divide-y divide-neutral-800 overflow-hidden rounded-lg border border-neutral-800">
            {hilos.map((h) => {
                const activo = String(h.id) === elegido
                return (
                    <li key={h.id}>
                        <button
                            type="button"
                            onClick={() => alElegir(h.id)}
                            className={
                                'flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left ' +
                                (activo ? 'bg-sky-500/15 text-sky-100' : 'text-neutral-300 hover:bg-neutral-900')
                            }
                        >
                            <span className="text-sm font-medium">
                                {h.ultima ? fechaHora(h.ultima) : 'sin fecha'}
                            </span>
                            {h.estado && (
                                <span className="text-[11px] text-neutral-500">{h.estado}</span>
                            )}
                        </button>
                    </li>
                )
            })}
        </ul>
    )
}

export function DetalleHiloCliente({
    accountId,
    contactId,
    convId,
}: {
    accountId: string | undefined
    contactId: string | undefined
    convId: number
}) {
    const q = useQuery<DetalleHilo>({
        queryKey: ['cliente-hilo', accountId, contactId, convId],
        queryFn: () =>
            api.get<DetalleHilo>(
                `/business/${accountId}/clientes/${contactId}/conversaciones/${convId}`,
            ),
        enabled: Number.isInteger(convId) && convId > 0,
    })
    useRefrescoHiloAbierto(() => {
        void q.refetch()
    })
    const [marca, setMarca] = useState<{ i: number; k: number } | null>(null)

    if (q.isPending) return <p className="text-sm text-neutral-500">Cargando el hilo…</p>
    if (q.isError || !q.data) {
        return <p className="text-sm text-rose-400">No se pudo cargar este hilo.</p>
    }

    const msgs = q.data.messages.status === 'ok' ? (q.data.messages.data ?? []) : []
    const indices = emparejarPasosMensajes(msgs, q.data.camino ?? [])

    const irAlPaso = (iPaso: number) => {
        const i = indices[iPaso]
        if (i === null || i === undefined) return
        const k = Date.now()
        setMarca({ i, k })
        window.setTimeout(() => {
            setMarca((m) => (m && m.k === k ? null : m))
        }, 2800)
    }

    return (
        <div className="grid gap-4 lg:grid-cols-2">
            <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Mensajes
                </h3>
                <MensajesConversacion
                    sobre={q.data.messages}
                    onReintentar={() => void q.refetch()}
                    recargando={q.isFetching}
                    compacto
                    resaltar={marca?.i ?? null}
                    resaltarClave={marca?.k ?? 0}
                    idHilo={convId}
                />
                {accountId && (
                    <ControlDelHilo
                        accountId={Number(accountId)}
                        conversationId={convId}
                        mando={q.data.mando}
                        onHecho={() => void q.refetch()}
                    />
                )}
            </section>
            <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Paso a paso del bot
                </h3>
                <div className="max-h-[42vh] overflow-y-auto pr-1">
                    {q.data.gestion && (
                        <div
                            className={cn(
                                'mb-3 rounded-lg border px-3 py-2 text-xs',
                                CAJA_CONSEGUIDO[q.data.gestion.tipo],
                            )}
                        >
                            {fraseGestion(q.data.gestion)}
                            <AvisoCitaAgenda cita={q.data.citaEnAgenda} />
                        </div>
                    )}
                    {(q.data.camino ?? []).length === 0 ? (
                        <p className="text-xs text-neutral-600">
                            Aún no hay camino apuntado para esta conversación.
                        </p>
                    ) : (
                        <ol className="space-y-1">
                            {(q.data.camino ?? []).map((p, i) => {
                                const casable = indices[i] != null
                                const clase = cn(
                                    'flex w-full items-start gap-2 rounded px-1 py-1 text-left text-xs',
                                    casable
                                        ? 'cursor-pointer bg-amber-400/10 ring-1 ring-amber-300/40 hover:bg-amber-400/20'
                                        : 'cursor-default opacity-45',
                                )
                                const interior = (
                                    <>
                                        <span
                                            className={cn(
                                                'mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full',
                                                p.tono ? PUNTO_CONSEGUIDO[p.tono] : 'bg-neutral-600',
                                            )}
                                        />
                                        <span className={p.tono ? TEXTO_CONSEGUIDO[p.tono] : 'text-neutral-300'}>
                                            {p.etiqueta}
                                        </span>
                                        {p.at && (
                                            <span className="ml-auto shrink-0 font-mono text-neutral-600">{hora(p.at)}</span>
                                        )}
                                    </>
                                )
                                return (
                                    <li key={i}>
                                        {casable ? (
                                            <button
                                                type="button"
                                                onClick={() => irAlPaso(i)}
                                                data-testid={`paso-camino-${i}`}
                                                className={clase}
                                            >
                                                {interior}
                                            </button>
                                        ) : (
                                            <div data-testid={`paso-camino-${i}`} className={clase}>
                                                {interior}
                                            </div>
                                        )}
                                    </li>
                                )
                            })}
                        </ol>
                    )}
                    {q.data.handovers.length > 0 && (
                        <div className="mt-3 space-y-1">
                            {q.data.handovers.map((h, i) => (
                                <div key={i} className="flex items-center gap-2 text-xs">
                                    <Badge tone="danger">{h.reason}</Badge>
                                    {h.stateAtHandover && (
                                        <span className="text-neutral-500">en {h.stateAtHandover}</span>
                                    )}
                                    <span className="ml-auto font-mono text-neutral-600">{hora(h.at)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <ResultadoCorto audit={q.data.audit} />
                </div>
            </section>
        </div>
    )
}

function fraseGestion(g: GestionHilo): string {
    const acto =
        g.tipo === 'reserva' ? 'Cita creada' : g.tipo === 'cancelacion' ? 'Cita anulada' : 'Cita cambiada'
    const cuando = g.fecha && g.hora ? ` ${cuandoCita(g.fecha, g.hora)}` : ''
    const con = g.profesional ? `, con ${g.profesional}` : ''
    return `${acto}${cuando}${con}`
}

function ResultadoCorto({ audit }: { audit: EventoAuditoriaVista[] }) {
    if (audit.length === 0) return null
    return (
        <ul className="mt-3 space-y-1 border-t border-neutral-800 pt-2">
            {audit.map((a, i) => (
                <li key={i} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="text-neutral-200">{ETIQUETA_EVENTO[a.event] ?? a.event}</span>
                    {a.slot?.['fecha'] && (
                        <span className="text-neutral-400">
                            {a.slot['fecha']}
                            {a.slot['hora'] ? ` · ${a.slot['hora']}` : ''}
                        </span>
                    )}
                    {a.at && <span className="ml-auto font-mono text-neutral-600">{fechaHora(a.at)}</span>}
                </li>
            ))}
        </ul>
    )
}
